'use client';

import type React from 'react';
import { createContext, useContext } from 'react';

import { ConsentStatus } from '../domain/types';

/**
 * Context value containing consent requirement information
 */
interface ConsentContextValue {
  /**
   * Whether the user requires consent based on their location
   * True for users in strict consent states (e.g., California)
   */
  requiresConsent: boolean;
  /**
   * The initial consent status fetched from the server/cookie
   */
  initialStatus: ConsentStatus;
}

const ConsentContext = createContext<ConsentContextValue>({
  requiresConsent: true, // Default to strictest setting
  initialStatus: ConsentStatus.UNKNOWN,
});

/**
 * Hook to access whether the current user requires consent
 *
 * @returns Object containing requiresConsent boolean
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { requiresConsent } = useConsentContext();
 *   return requiresConsent ? <ConsentBanner /> : null;
 * }
 * ```
 */
export function useConsentContext() {
  return useContext(ConsentContext);
}

export interface ConsentContextProviderProps {
  children: React.ReactNode;
  requiresConsent: boolean;
  initialStatus?: ConsentStatus;
}

/**
 * Provider that makes requiresConsent available to all child components
 *
 * This allows server-determined consent requirements (based on geolocation)
 * to flow down to client components
 *
 * @param props - Component props
 * @param props.children - Child components
 * @param props.requiresConsent - Whether the user requires consent (based on location)
 * @param props.initialStatus - The initial consent status (from server cookie)
 *
 * @example
 * ```typescript
 * // In server layout
 * const requiresConsent = isInStrictConsentState({ stateAcronym: 'CA' });
 * return (
 *   <ConsentContextProvider requiresConsent={requiresConsent} initialStatus={status}>
 *     {children}
 *   </ConsentContextProvider>
 * );
 * ```
 */
export function ConsentContextProvider({
  children,
  requiresConsent,
  initialStatus = ConsentStatus.UNKNOWN,
}: ConsentContextProviderProps) {
  return (
    <ConsentContext.Provider value={{ requiresConsent, initialStatus }}>
      {children}
    </ConsentContext.Provider>
  );
}
